import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Server-authoritative tenant scope resolver.
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 *   - who the current user is,
 *   - whether they hold platform-admin privilege (unscoped super_admin/admin),
 *   - which tenants they can legitimately view,
 *   - which tenants they administer,
 *   - which tenant should be selected by default.
 *
 * The client MUST NOT recompute any of these from role rows read directly from
 * the browser. localStorage may hint at a preferred tenant, but the server
 * decides whether that hint is valid. If localStorage disagrees with the
 * server, the server always wins — the client renders a loading state until
 * this resolves rather than displaying a scope the server has not authorized.
 *
 * See docs/security/tenant-isolation-report.md (H4) and the tenant-isolation
 * architecture doc for the principles this enforces.
 */

export type TenantScope = {
  userId: string;
  isPlatformAdmin: boolean;
  /** Tenant IDs the user is allowed to view. Platform admins see all non-test tenants. */
  visibleTenantIds: string[];
  /** Tenant IDs where the user holds admin/tenant_admin (excludes platform-wide). */
  tenantAdminOf: string[];
  /** Server-preferred default tenant (profiles.default_tenant_id if valid, else first visible). */
  defaultTenantId: string | null;
  /** Resolved timestamp — clients can use this to detect stale caches. */
  resolvedAt: string;
};

export const resolveTenantScope = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<TenantScope> => {
    const { supabase, userId } = context;

    // 1) Platform-admin check goes through the SECURITY DEFINER helper we
    //    established in the H3 fix. This is the ONLY sanctioned unscoped
    //    role check anywhere in the codebase.
    const { data: isPlatformAdminRaw, error: adminErr } = await supabase.rpc(
      "is_platform_admin",
      { _user_id: userId },
    );
    if (adminErr) throw new Error(`tenant-scope: is_platform_admin failed: ${adminErr.message}`);
    const isPlatformAdmin = !!isPlatformAdminRaw;

    // 2) Read user_roles under the caller's own token (RLS applies). The
    //    user_roles policy already restricts a user to their own rows plus
    //    manager-visible rows for their tenants; we only care about the
    //    caller's rows here.
    const { data: roleRows, error: rolesErr } = await supabase
      .from("user_roles")
      .select("role, tenant_id")
      .eq("user_id", userId);
    if (rolesErr) throw new Error(`tenant-scope: user_roles read failed: ${rolesErr.message}`);

    const tenantAdminOf = Array.from(
      new Set(
        (roleRows ?? [])
          .filter(
            (r) =>
              r.tenant_id &&
              (r.role === "admin" ||
                r.role === "tenant_admin" ||
                r.role === "super_admin"),
          )
          .map((r) => r.tenant_id as string),
      ),
    );

    const tenantScopedIds = Array.from(
      new Set(
        (roleRows ?? [])
          .filter((r) => r.tenant_id)
          .map((r) => r.tenant_id as string),
      ),
    );

    // 3) Visible tenants:
    //    - platform admins: every non-test tenant
    //    - everyone else: only tenants they have a role in
    //    Either way, RLS is the ultimate gate — this is authorization for the
    //    tenant *switcher*, not for row reads.
    let visibleTenantIds: string[];
    if (isPlatformAdmin) {
      const { data: allTenants, error: tenantsErr } = await supabase
        .from("tenants")
        .select("id")
        .eq("is_test", false);
      if (tenantsErr) throw new Error(`tenant-scope: tenants read failed: ${tenantsErr.message}`);
      visibleTenantIds = (allTenants ?? []).map((t) => t.id as string);
    } else {
      visibleTenantIds = tenantScopedIds;
    }

    // 4) Default tenant: prefer profiles.default_tenant_id when it is inside
    //    the visible set; otherwise pick the first visible tenant.
    const { data: profile } = await supabase
      .from("profiles")
      .select("default_tenant_id")
      .eq("id", userId)
      .maybeSingle();
    const preferred = profile?.default_tenant_id ?? null;
    const defaultTenantId =
      preferred && visibleTenantIds.includes(preferred)
        ? preferred
        : (visibleTenantIds[0] ?? null);

    return {
      userId,
      isPlatformAdmin,
      visibleTenantIds,
      tenantAdminOf,
      defaultTenantId,
      resolvedAt: new Date().toISOString(),
    };
  });